import { z } from "zod";
import { LibraryCompletedEngagementSchema } from "./casebook.js";
import { EnvironmentViewSchema } from "./environment-view.js";
import { ExperienceLevelSchema, LearnerProfileSchema, ProfileDraftSchema, ProfileQuestionSchema, RehearsalPartsSchema } from "./learner.js";
import { PressureContextSchema, RubricDimensionSchema, TrackSchema } from "./primitives.js";
import { EvidenceReferencesSchema } from "./coach-evidence.js";
import { PracticeBriefSchema, PracticeStateSchema } from "./practice.js";
import { FocusViewSchema } from "./coach-focus.js";
/**
 * HTTP request/response body schemas for apps/api routes (apps/api task
 * section 3: "schemas from contracts"). Kept separate from the domain
 * schemas above them (events, check-spec, case-bible, ...) because these
 * describe WIRE shapes for a specific route, not persisted or engine state.
 */
export const GuestCreatedSchema = z.strictObject({
    guestId: z.string().min(1),
    /** True when the browser already held a live guest identity and it was kept rather than replaced. */
    reused: z.boolean(),
});
/** POST /auth/guest body. `fresh: true` asks for a new identity even when the browser holds one. */
export const GuestCreateRequestSchema = z.strictObject({ fresh: z.boolean().optional() });
/**
 * GET /me/identity: who the server takes this browser to be, from its cookies
 * alone. `learnerId` is the id the other /me surfaces key on (the user id once
 * signed in, the guest id before) and null when nobody is signed in. It lets
 * the client decide whether the landing page is the right door at all.
 */
export const WhoAmISchema = z.strictObject({
    kind: z.enum(["user", "guest", "none"]),
    learnerId: z.string().min(1).nullable(),
    /**
     * A signed-in account's pending deletion (docs/ACCOUNT-ERASURE-DESIGN-2026-10-20.md):
     * when it is set the client shows the grace notice instead of the app, and
     * every other route answers 403 ACCOUNT_ERASURE_PENDING. Null for a guest,
     * for nobody, and for an account with nothing pending.
     */
    erasure: z.strictObject({ eraseAfter: z.iso.datetime() }).nullable(),
    /**
     * The terms (Andre's decisions of 2026-10-22): true for a guest and for
     * nobody, whose play the notice above every sign-in door covers; for an
     * account, whether its latest recorded acceptance is of TERMS_VERSION (the
     * accepted version, not the page's date line, TERMS_UPDATED). An
     * account with no acceptance on record (one made before acceptance was
     * recorded, or whose sign-up write failed) and one that accepted an earlier
     * version are both false, and the client asks it to accept the current
     * terms again. Never a gate on the server: an answer the client acts on.
     */
    termsCurrent: z.boolean(),
});
/**
 * SELF-SERVE ACCOUNT ERASURE (docs/ACCOUNT-ERASURE-DESIGN-2026-10-20.md, Andre's
 * decisions of 2026-10-20). The problem code every route but /me/identity and
 * /me/erasure answers a signed-in account with while its deletion is pending
 * (403): the client reads it as "ask /me/identity again", which shows the
 * grace notice.
 */
export const ACCOUNT_ERASURE_PENDING_CODE = "ACCOUNT_ERASURE_PENDING";
/**
 * POST /me/erasure: the two copies the learner asks to be emailed with the
 * confirmation. They are kept only for a verified address (ruling 17); an
 * unverified one is offered the downloads instead, and its choices are
 * recorded as none.
 */
export const AccountErasureRequestSchema = z.strictObject({
    copyCoaching: z.boolean(),
    copyCasebooks: z.boolean(),
});
/** GET /me/erasure: the pending request, or null, and whether the address is verified (the dialog offers emailed copies only then). */
export const AccountErasureStatusSchema = z.strictObject({
    erasure: z
        .strictObject({
        /** The deletion runs after this moment. */
        eraseAfter: z.iso.datetime(),
        /** The address was verified when the learner asked: the chosen copies go by email only then. */
        copiesByEmail: z.boolean(),
        copyCoaching: z.boolean(),
        copyCasebooks: z.boolean(),
    })
        .nullable(),
    emailVerified: z.boolean(),
});
/**
 * POST /me/erasure's answer. Every sign-in of the account has ended by the
 * time it arrives. `emailQueued` is true while the confirmation waits in the
 * outbox: mail goes out only once the transport is configured, so the client
 * says it is "on its way", never that it was sent. A second request while one
 * stands returns the first unchanged.
 */
export const AccountErasureRequestedSchema = z.strictObject({
    eraseAfter: z.iso.datetime(),
    copiesByEmail: z.boolean(),
    emailQueued: z.boolean(),
});
/** DELETE /me/erasure: "keep my account."; `cancelled` is false when nothing was pending. */
export const AccountErasureCancelledSchema = z.strictObject({ cancelled: z.boolean() });
export const ClaimRequestSchema = z.strictObject({ email: z.email() });
export const ClaimedSchema = z.strictObject({ userId: z.string().min(1), movedSessionIds: z.array(z.string().min(1)) });
/**
 * THE INTAKE (onboarding pass, 2026-09-13). The first call opens an intake
 * with the learner's prose; every later call on the same `intakeId` is the
 * answer to the question the server asked, or a correction to the read-back.
 * The server folds each into one draft and decides, in code, what is still
 * missing.
 */
export const ProfileExtractRequestSchema = z.strictObject({
    onboardingProse: z.string().min(1).max(4000),
    intakeId: z.string().min(1).optional(),
});
export const ProfileExtractedSchema = z.strictObject({
    intakeId: z.string().min(1),
    /** What has been taken from the learner's words so far, each piece with the words behind it. */
    draft: ProfileDraftSchema,
    /** The one thing still needed before the read-back, or null when the draft can be read back and confirmed. */
    question: ProfileQuestionSchema.nullable(),
    /** The complete, unconfirmed profile and its row, present only when `question` is null. */
    learnerProfileId: z.string().min(1).nullable(),
    profile: LearnerProfileSchema.nullable(),
    /** "model" when the extractor read the last line; "fallback" when it could not and the draft was left as it was (Law 4). */
    source: z.enum(["model", "fallback"]),
    /**
     * THE COACH'S OWN WORDS (AJ intake, 2026-09-24): what it said back, in one
     * to three paragraphs, asking for the one thing code found missing or
     * leading into the read-back. Kept in the conversation too, so a reload
     * reads the same. Empty when the intake is being restored, since the
     * conversation already holds the words.
     */
    reply: z.array(z.string().min(1)).max(3),
    /** "model" when the coach wrote the reply; "authored" when the deterministic line did (Law 4). */
    replySource: z.enum(["model", "authored"]),
});
/** GET /profile/intake: the open intake to pick up after a reload, or null when there is none or the profile is already confirmed. */
export const ProfileIntakeRestoreSchema = z.strictObject({ intake: ProfileExtractedSchema.nullable() });
/** POST /profile/confirm: the profile is kept, and the coach says what it has put on the desk. */
export const ProfileConfirmedSchema = z.strictObject({
    confirmed: z.literal(true),
    /** The coach's line about the papers, one to three paragraphs; also kept in the conversation. */
    papers: z.array(z.string().min(1)).min(1).max(3),
    /** Whether a first focus was laid on the desk from what the learner said they would work on first. */
    focusPlaced: z.boolean(),
});
/**
 * A closed-set answer, applied by code with no model call: the learner
 * picked one of the offered experience levels, or typed a name after the
 * extractor had used its rounds.
 */
export const ProfileAnswerRequestSchema = z.strictObject({
    intakeId: z.string().min(1),
    experience_level: ExperienceLevelSchema.optional(),
    name: z.string().min(1).max(120).optional(),
});
export const ProfileConfirmRequestSchema = z.strictObject({ learnerProfileId: z.string().min(1) });
/** The rehearsal move (onboarding pass, idea 9): one line from the learner, one reflection back. */
export const RehearsalRequestSchema = z.strictObject({ learnerProfileId: z.string().min(1), text: z.string().min(1).max(4000) });
export const RehearsalReflectionSchema = z.strictObject({
    /** Which of the three parts of a move were there, each as the learner's own words or null. */
    parts: RehearsalPartsSchema,
    /** The coach's authored reflection, chosen by which parts were present, quoting the learner. */
    lines: z.array(z.string().min(1)).min(1),
    source: z.enum(["model", "fallback"]),
});
export const TrackListItemSchema = z.strictObject({ track: TrackSchema, title: z.string().min(1) });
export const TracksResponseSchema = z.array(TrackListItemSchema);
/**
 * One published, registered case. `track`, `client`, `primaryDimension` and
 * `pressureContext` come from the authored bible — the library metadata
 * docs/case-authoring/04 §6 says the library surface reads — so a card can
 * carry its track, goal and skill without a second request per case. Nothing
 * solution-adjacent crosses here: no mechanism family, no size profile.
 */
export const CaseListItemSchema = z.strictObject({
    id: z.string().min(1),
    slug: z.string().min(1),
    semver: z.string().min(1),
    track: TrackSchema,
    client: z.string().min(1),
    primaryDimension: RubricDimensionSchema,
    pressureContext: PressureContextSchema,
    /**
     * The bible's industry and engagement question, the two opening fields a
     * card shows (2026-09-24): with them here the library is one request, not
     * one more per case, which spent the rate limit on every Home load.
     */
    industry: z.string().min(1),
    engagementQuestion: z.string().min(1),
});
export const CasesResponseSchema = z.array(CaseListItemSchema);
/**
 * POST /sessions opens a case, and a learner has AT MOST ONE open run of a
 * case at a time (library pass, 2026-09-08). Without `replaceOpen` an
 * existing open run is handed back (`resumed: true`, 200) rather than a
 * second one started beside it; with `replaceOpen: true` every open run of
 * that case is abandoned and a fresh one begins (`resumed: false`, 201).
 * The client asks "are you sure" before it sends `replaceOpen`.
 */
export const CreateSessionRequestSchema = z.strictObject({ caseSlug: z.string().min(1), replaceOpen: z.boolean().optional() });
export const SessionCreatedSchema = z.strictObject({
    sessionId: z.string().min(1),
    stateVersion: z.number().int().min(0),
    /** True when this is the run the learner already had open, not a new one. */
    resumed: z.boolean(),
});
export const SessionStateResponseSchema = z.strictObject({
    sessionId: z.string().min(1),
    /** Which case this session plays (its registry slug), so a client with several cases knows whose opening to show. Null only for a session whose case row is gone. */
    caseSlug: z.string().min(1).nullable(),
    status: z.string().min(1),
    currentBeatId: z.string().nullable(),
    situationPressure: z.string().nullable(),
    narration: z.array(z.strictObject({ turn: z.number().int(), text: z.string() })),
    documentsDelivered: z.array(z.strictObject({ docId: z.string(), kind: z.string(), title: z.string(), fidelity: z.string() })),
    factsRevealed: z.array(z.strictObject({ factId: z.string(), text: z.string(), reliability: z.string() })),
    npcs: z.array(z.strictObject({ npcName: z.string(), role: z.string(), relationship: z.string(), disposition: z.string() })),
    hypotheses: z.array(z.strictObject({ text: z.string() })),
    /** The living notes (progress surface, ADR 0008): what the learner has established, and what remains open — prose, in their own voice. */
    notes: z.strictObject({ established: z.array(z.string()), open: z.array(z.string()) }),
    /**
     * The server-derived environment scene (ADR 0009 commitment 1), shipped
     * whole and already physical — the client draws it, derives nothing.
     * Null for sessions whose case has no environment authored. Unlike the
     * widened-string fields above, this stays fully typed: it contains no
     * mechanical value to widen (that is enforced at its definition).
     */
    environment: EnvironmentViewSchema.nullable(),
    /**
     * The partner's cross-examination, once a recommendation has been presented
     * (status ENDGAME_CROSS_EXAM onward). Derived from the event log so a reload
     * lands on the same question; null before the recommendation. `question` is
     * null once all four have been answered.
     */
    crossExam: z
        .strictObject({
        recommendationText: z.string(),
        exchanges: z.array(z.strictObject({ questionNumber: z.number().int().min(1).max(4), question: z.string(), answer: z.string() })),
        question: z.strictObject({ questionNumber: z.number().int().min(1).max(4), text: z.string() }).nullable(),
    })
        .nullable(),
});
/**
 * GET /sessions/:id/turns (story-legibility phase, 3a) — the learner-facing
 * story thread: every turn's own input and what came back, in order,
 * assembled server-side from session_events so it survives reload/resume.
 * Added to the contract because NO route previously returned the learner's
 * own text — INTENT_DECLARED lived only in the event log, so the client
 * could not render a transcript at all.
 */
export const TurnHistoryEntrySchema = z.strictObject({
    turn: z.number().int().min(0),
    /**
     * The chapter of the in-game day this turn ended on: the coded slug line of the moment that date's first turn ended at,
     * "Monday, April 6, 8:40 a.m." (contracts' fiction clock via the engine's dayChapters; 2026-10-29), never an
     * authored beat title. The story turns a page where it changes, so once per in-game day. Null when the
     * case is not in the registry; absent from older servers.
     */
    chapter: z.string().nullable().optional(),
    /** The learner's own message; null for turn 0 (the opening desk) and engine-initiated turns. */
    learnerText: z.string().nullable(),
    narration: z.array(z.string()),
    documents: z.array(z.strictObject({ docId: z.string(), kind: z.string(), title: z.string(), fidelity: z.string() })),
    /** Diegetic NPC tells, same text the SSE NPC_STATE_CHANGED envelope carries. */
    npcTells: z.array(z.string()),
    /** The opt-in "show mechanics" line for this turn's roll; present only when the owner's setting is on, null otherwise (Law 2). */
    mechanics: z.string().nullable().optional(),
});
export const SessionTurnsResponseSchema = z.strictObject({
    sessionId: z.string().min(1),
    turns: z.array(TurnHistoryEntrySchema),
});
export const SessionIdParamsSchema = z.strictObject({ id: z.string().min(1) });
/**
 * GET /sessions/:id/documents (desk pass, 2026-09-03) — the desk. Every
 * delivered document with its CARD FACE: the real title and a one-line
 * preview, both computed server-side from the fidelity-transformed document
 * (the same CardSummary the render plan carries). The client used to fetch
 * every document's full render plan just to label its card, and fetching a
 * plan IS an open under ADR 0008 — so every document was recorded as read
 * the moment it landed. Listing is not reading: this route records nothing.
 */
export const SessionDocumentSummarySchema = z.strictObject({
    docId: z.string().min(1),
    kind: z.string().min(1),
    title: z.string().min(1),
    preview: z.string(),
    fidelity: z.string().min(1),
    deliveredAtTurn: z.number().int().min(0),
});
export const SessionDocumentsResponseSchema = z.array(SessionDocumentSummarySchema);
export const SessionDocumentParamsSchema = z.strictObject({ id: z.string().min(1), did: z.string().min(1) });
export const TurnRequestSchema = z.strictObject({ learnerText: z.string().min(1) });
export const RecommendationRequestSchema = z.strictObject({ text: z.string().min(1) });
export const RecommendationAcceptedSchema = z.strictObject({ ready: z.literal(true), firstQuestion: z.string() });
export const RecommendationNotReadySchema = z.strictObject({ ready: z.literal(false), message: z.string() });
/**
 * `showMechanics` is the ADR-0001 opt-in (default FALSE, Law 2). `readAloud`
 * asks for each turn's narration to be voiced as it lands (ADR 0011); it
 * defaults off because audio that starts unasked is an interruption, and it
 * is honoured only where the deployment has a narration voice at all.
 */
/**
 * CME stage 1 (ADR 0021). The two email purposes a signed-in learner can
 * agree to, both NOT granted until they say so, and the two optional facts
 * about where they are recruiting from. Program and season live on the
 * learner's profile; nothing in a case reads them.
 */
/** Stage 1's two purposes, still the whole of a CME contact's ledger (0035). A learner's own ledger holds `UserConsentPurposeSchema`. */
export const ConsentPurposeSchema = z.enum(["MARKETING_EMAIL", "OUTCOME_CHECKIN"]);
/**
 * EMAIL CHOICES (docs/cme/EMAIL-CHOICES-PLAN-2026-10-07.md, migration 0037).
 * The seven update purposes the coach's card and settings offer, every one
 * off until the learner turns it on; the two functional purposes asked just
 * in time, before the first such email; and the full set the ledger can hold,
 * which keeps stage 1's MARKETING_EMAIL readable. Nothing offers that one any
 * more, and a past yes to it selects none of these.
 */
export const EmailUpdatePurposeSchema = z.enum(["NEW_CASE", "MAJOR_UPDATE", "COACH_WEEKLY", "NEW_FROM_CREATORS", "SEASON_REMINDERS", "RESEARCH_INVITES", "OUTCOME_CHECKIN"]);
export const EmailFunctionalPurposeSchema = z.enum(["CERTIFICATES", "RESULTS"]);
export const UserConsentPurposeSchema = z.enum(["MARKETING_EMAIL", ...EmailUpdatePurposeSchema.options, ...EmailFunctionalPurposeSchema.options]);
/** When a weekly note from the coach would come: the grant records one, and neither is chosen in advance. */
export const CoachWeeklyTimingSchema = z.enum(["ALL_YEAR", "RECRUITING_SEASON"]);
/**
 * The seven update choices, saved whole in one request. A weekly note asked
 * for carries its timing; one not asked for carries none, so the ledger never
 * records a timing nobody chose.
 */
export const EmailChoicesSchema = z
    .strictObject({
    NEW_CASE: z.boolean(),
    MAJOR_UPDATE: z.boolean(),
    COACH_WEEKLY: z.boolean(),
    NEW_FROM_CREATORS: z.boolean(),
    SEASON_REMINDERS: z.boolean(),
    RESEARCH_INVITES: z.boolean(),
    OUTCOME_CHECKIN: z.boolean(),
    coachWeeklyTiming: CoachWeeklyTimingSchema.nullable(),
})
    .refine((c) => c.COACH_WEEKLY === (c.coachWeeklyTiming !== null), { message: "a weekly note needs its timing, and only a weekly note has one", path: ["coachWeeklyTiming"] });
/** Every update purpose off: what "no emails." saves, and what a learner who has said nothing reads as. */
export const NO_EMAIL_CHOICES = {
    NEW_CASE: false,
    MAJOR_UPDATE: false,
    COACH_WEEKLY: false,
    NEW_FROM_CREATORS: false,
    SEASON_REMINDERS: false,
    RESEARCH_INVITES: false,
    OUTCOME_CHECKIN: false,
    coachWeeklyTiming: null,
};
/**
 * GET /me/email-choices. Nothing about email appears until the learner's
 * first Harborlight report exists (Andre, 2026-10-07): before then the answer
 * is `eligible: false` and carries no choices. A guest who has finished
 * Harborlight is eligible and sees the card, but holds no choices: a guest has
 * no address, and the card says sign in.
 *
 * `addressVerified` (Andre's ruling 17, 2026-10-12): mail goes only to a
 * verified address (`users.email_verified`, the rule every mail pool applies),
 * so a signed-in learner whose address is not verified can save choices that
 * nothing will act on. Until sign-in verification ships, the card and
 * settings say so in one sentence when this is false.
 */
export const EmailChoicesStateSchema = z.union([
    z.strictObject({ eligible: z.literal(false) }),
    z.strictObject({ eligible: z.literal(true), signedIn: z.literal(false) }),
    z.strictObject({ eligible: z.literal(true), signedIn: z.literal(true), addressVerified: z.boolean(), choices: EmailChoicesSchema }),
]);
/**
 * A just-in-time functional ask (CERTIFICATES, RESULTS): one question, neither
 * answer chosen, put just before that kind of email would first be sent. The
 * contract exists; nothing puts the question yet, because no such email
 * exists and sending is held.
 */
export const FunctionalEmailAnswerSchema = z.strictObject({ purpose: EmailFunctionalPurposeSchema, granted: z.boolean() });
/**
 * Where a consent row came from. The last two (2026-10-21, migration 0047) are
 * the mail provider's: Mailjet replaces the List-Unsubscribe pair we set with
 * its own, so a mail app's Unsubscribe button (PROVIDER_UNSUBSCRIBE, Mailjet's
 * "unsub" event) and a spam report (PROVIDER_COMPLAINT, its "spam" event)
 * reach Mailjet first, and its Event API tells the api, which withdraws every
 * email choice for the accounts at that address. They only ever withdraw; the
 * database holds that too.
 */
export const ConsentSourceSchema = z.enum(["SETTINGS", "UNSUBSCRIBE_LINK", "OPERATOR_RECORDED", "PROVIDER_UNSUBSCRIBE", "PROVIDER_COMPLAINT"]);
export const ProgramSchema = z.enum(["UNDERGRAD", "MBA", "OTHER_GRAD", "NONE", "UNSTATED"]);
/**
 * The privacy page's date line, recorded on every consent row as the policy
 * the learner answered under. The server's constant, never the client's word.
 * `PRIVACY_POLICY_VERSION` in apps/web/src/design/copy.ts carries the same
 * line beside PRIVACY, and a web test holds the two equal: change both, in the
 * release that changes the page.
 */
// The Z.ai release (2026-10-29): the earliest day it can ship; moved with the pins if it ships later.
export const PRIVACY_POLICY_VERSION = "Last updated October 31, 2026.";
/**
 * THE TERMS' VERSION, APART FROM THE PAGE'S DATE (Andre's decisions of
 * 2026-10-22; the review of 2026-10-22). Two constants, because they change
 * for different reasons:
 *
 * - TERMS_VERSION is what an account accepts: recorded on every
 *   terms_acceptances row (migration 0048) and compared with the latest row
 *   for GET /me/identity's `termsCurrent`. The server's constant, never the
 *   client's word. Moved ONLY for a change that matters to people's rights,
 *   since moving it asks every account to accept again (the terms say so).
 * - TERMS_UPDATED is the terms page's date line, which may move for any edit,
 *   a typo fix included, and asks no one anything.
 *
 * The web's terms page prints TERMS_UPDATED. Both dates are set at deploy.
 *
 * 2026-10-22 was the first version. Andre's ruling 3 of 2026-10-29: the coach's replies and
 * greeting going to Z.ai directly is a change that matters to people's rights, so every account
 * accepts again, once: TERMS_VERSION moves past 2026-10-22, which every account holds, to the Z.ai
 * release's date, its deploy's (2026-10-31, UTC; set first at 2026-10-30, the earliest day it
 * could ship, and moved when the deploy's switchover moved past 00:00 UTC). If the deploy is later
 * still, both move to its date with their pins (docs/COACH-ZAI-DIRECT-2026-10-29.md, "The date
 * lines"): no account can hold an acceptance of a version before it ships, so that asks no one twice.
 */
export const TERMS_VERSION = "2026-10-31";
export const TERMS_UPDATED = "Last updated October 31, 2026.";
/**
 * THE WEB APP'S OWN FETCH NAMES ITSELF (the review of 2026-10-22). The
 * confirmation link's call, GET /auth/verify-email, signs a browser in; a
 * page on another site could send a visitor there in a link or a form and
 * sign them into an account it controls, and the visitor's guest work would
 * then move into it. A cross-site page cannot add a custom header to a
 * navigation or to a request the browser sends without asking (a "simple"
 * request), and this API answers no cross-origin preflight, so the api
 * refuses that GET unless this header carries this value
 * (apps/api/src/routes/account.ts). packages/client's verifyEmail sends it.
 * Lower case, as a Headers object and Fastify's request.headers read it.
 */
export const PNC_CLIENT_HEADER = "x-pnc-client";
export const PNC_CLIENT_WEB = "web";
/**
 * Where a terms acceptance came from: the account's sign-up (email and
 * password, or Google), or the account accepting a later version again.
 * RECLAIM is the ledger's one row that is no acceptance (the pre-release
 * review of 2026-10-22, round two): where a password reset proved the mailbox
 * of an account that was unconfirmed until then. The acceptances before it
 * may be whoever typed the sign-up's, so none holds until one is made after
 * it. Checkout (the paywall plan's third source) is not here: nothing is sold
 * yet.
 */
export const TermsAcceptanceSourceSchema = z.enum(["SIGN_UP", "REACCEPT", "RECLAIM"]);
/** A recruiting season written `YYYY-YY`, e.g. `2026-27` (the database holds the same pattern). */
export const TargetSeasonSchema = z.string().regex(/^[0-9]{4}-[0-9]{2}$/);
/** The email choices left settings' own body for /me/email-choices (2026-10-07); program and season stay here. */
export const SettingsSchema = z.strictObject({
    showMechanics: z.boolean(),
    readAloud: z.boolean(),
    program: ProgramSchema.nullable(),
    targetSeason: TargetSeasonSchema.nullable(),
});
/** Program and season take `null` to clear. */
export const SettingsPatchSchema = z.strictObject({
    showMechanics: z.boolean().optional(),
    readAloud: z.boolean().optional(),
    program: ProgramSchema.nullable().optional(),
    targetSeason: TargetSeasonSchema.nullable().optional(),
});
/**
 * GET /me/library — the case library's per-learner state (library pass,
 * 2026-09-08). Everything here is assembled server-side from rows the learner
 * owns; no model runs on this path and nothing here is play state.
 *
 * `unlocked` is the ONE gate the library has (AJ intake, 2026-09-24): the
 * first case finished, the learner signed in rather than a guest, and the
 * first development plan read, which is written after that first case over
 * the reading and what the learner said they wanted to work on. Finishing
 * the first case alone opens the other calibration cases (`calibrationOpen`).
 * It is a progression gate on authored content order, never a skill gate —
 * the skill profile still adapts difficulty and never closes a door (Law 6).
 * The client greys the locked shelves and says in words what opens them;
 * the server does not refuse a session for a locked case.
 *
 * The two fractions on an open engagement feed unlabelled bars: how much of
 * the engagement's calendar has gone by, and how much of the case's evidence
 * has been found. Neither is a clock, a resource, or a mechanical value —
 * the calendar is derived from time-cost events the fiction already narrates
 * as days, and the evidence share from facts the learner already holds.
 */
export const LibraryOpenEngagementSchema = z.strictObject({
    sessionId: z.string().min(1),
    caseSlug: z.string().min(1),
    /** Share of the engagement's calendar already spent, in [0, 1]. */
    timeUsed: z.number().min(0).max(1),
    /** Whole days still to go before the deadline the case opens with. */
    daysLeft: z.number().int().min(0),
    /** Share of the case's facts the learner has been shown, in [0, 1]. */
    evidenceFound: z.number().min(0).max(1),
});
/**
 * THE GATE'S THREE CONDITIONS, each stated on its own so the client can say
 * in words which are still open. `firstCase` is the slug of the one case a
 * new learner is handed (Harborlight, always); it is carried here so no
 * client hard-codes it.
 */
export const LibraryGateSchema = z.strictObject({
    firstCase: z.string().min(1),
    firstCaseDone: z.boolean(),
    signedIn: z.boolean(),
    planAcknowledged: z.boolean(),
});
export const LibraryStateSchema = z.strictObject({
    /** Case slugs the learner has put in the saved box, in the order they were saved. */
    saved: z.array(z.string().min(1)),
    /** When the learner acknowledged their first development plan, or null. */
    planAcknowledgedAt: z.iso.datetime().nullable(),
    /** Calibration cases finished (distinct slugs). */
    calibrationCompleted: z.array(z.string().min(1)),
    /** Every case slug with a finished engagement, calibration or not. */
    completed: z.array(z.string().min(1)),
    /** Every finished engagement, newest first, each opening its casebook (2026-09-24). */
    completedEngagements: z.array(LibraryCompletedEngagementSchema),
    gate: LibraryGateSchema,
    /** The other calibration cases are open: the first case is finished. */
    calibrationOpen: z.boolean(),
    /** The whole library is open: all three of the gate's conditions hold. */
    unlocked: z.boolean(),
    openEngagements: z.array(LibraryOpenEngagementSchema),
});
export const LibrarySavedPutSchema = z.strictObject({ slugs: z.array(z.string().min(1)).max(50) });
/**
 * `learnerProfileId` is optional and ignored server-side: the coach resolves
 * the learner from the authenticated OWNER (auth/require.ts), never from a
 * client-supplied id — an id in the body could only ever disagree with the
 * cookie or agree with it, and neither is useful. Kept optional rather than
 * removed so older clients' requests stay schema-valid.
 */
export const CoachMessageRequestSchema = z.strictObject({ learnerProfileId: z.string().min(1).optional(), message: z.string().min(1) });
/**
 * THE COACH'S OPENING (home pass, 2026-09-09) — what the coach says first
 * when the learner arrives, and the cards it pushes out from behind its own
 * panel. GET /coach/opening.
 *
 * WHO DECIDES WHAT. The CARDS are chosen by deterministic code from the
 * learner's own shelf state and practice record (apps/api coach/opening-
 * cards.ts) — the model never picks a case, so a card can only ever name a
 * case the library already holds. The WELCOME is the Coach's prose, written
 * over the same immutable record the coach always reads, with the chosen
 * cards handed to it as input so it can speak to them. When the model's
 * answer fails validation the welcome is assembled from authored sentences
 * instead (Law 4), and `source` says which happened — for the audit tape and
 * for tests, never rendered.
 *
 * WHAT A CARD CANNOT SAY: a number. The card carries a kind, a case, and a
 * one-line reason in words; track, goal and skill are read off the case
 * list by slug, exactly as the library's cards do. No score, no rank, no
 * fraction rides on this contract.
 */
export const CoachCardKindSchema = z.enum([
    /** The one case the coach wants next: trains the lagging dimension, or the next calibration case while the library is locked. */
    "NEXT",
    /** An engagement the learner has open. */
    "CONTINUE",
    /** From the learner's own saved box. */
    "SAVED",
    /** A remaining calibration case, while the library is still locked. */
    "CALIBRATION",
    /** The first development plan, not yet read — the other half of the library's one gate. */
    "PLAN",
    /** A further case from the shelf, when there is nothing more specific to push. */
    "SHELF",
    /** A case the coach itself chose from the whole library, in conversation, for something the learner asked to work on. */
    "PUSHED",
]);
export const CoachCardSchema = z.strictObject({
    kind: CoachCardKindSchema,
    /** Null only on a PLAN card. */
    caseSlug: z.string().min(1).nullable(),
    /** Set on a CONTINUE card: the open run to walk back into. */
    sessionId: z.string().min(1).nullable(),
    /** The client's name, or the plan's own title. */
    title: z.string().min(1),
    /** Why this card, in one plain sentence. Authored per kind, never model-written. */
    reason: z.string().min(1),
    /**
     * THE LINKS ON A PUSHED CARD (2026-10-31, from the arrange-files ideation;
     * coach_pushes.message_id / focus_id, migration 0050). `messageId` is the
     * coach's reply whose words put the card on the desk, so the desk can open
     * the conversation there and draw the connection; `focusId` is the focus
     * the push was accepted as being for, so "my focus." can gather the card
     * without guessing from its skill. Both optional: an opening cached before
     * the links existed, and every card of another kind, carries neither, and
     * reads as an ordinary card. A missing link is never an error.
     */
    messageId: z.string().min(1).optional(),
    focusId: z.string().min(1).optional(),
});
/** At least two cards whenever the library holds two cases; never more than seven. */
export const COACH_OPENING_MAX_CARDS = 7;
export const COACH_OPENING_MIN_CARDS = 2;
export const CoachOpeningSchema = z.strictObject({
    /** Optional only for previously cached greetings and older clients. */
    kind: z.enum(["NEWCOMER", "FIRST_EVIDENCE", "RETURNING", "AWAITING_DEBRIEF"]).optional(),
    /** Proportionate to the available history; no fixed three-paragraph review. */
    welcome: z.array(z.string().min(1)).min(1).max(5),
    cards: z.array(CoachCardSchema).max(COACH_OPENING_MAX_CARDS),
    /** "model" when the Coach wrote the welcome; "authored" when the deterministic fallback did. */
    source: z.enum(["model", "authored"]),
    generatedAt: z.iso.datetime(),
});
/**
 * PRACTICE (coach plan steps 6 to 8; C09, C06, C07): what the client sees of
 * an exercise. The brief's learner-facing fields, the engine's projection,
 * and the authored feedback lines. Never the markers (they are the heuristic
 * reading's business) and never a reading of the learner's record: finishing
 * an exercise changes nothing there.
 */
export const PracticeBriefViewSchema = PracticeBriefSchema.omit({ criteria: true, counterpart: true, variations: true }).extend({
    criteria: z.array(z.strictObject({ id: z.string().min(1), text: z.string().min(1) })),
    counterpart: z.strictObject({ name: z.string().min(1), role: z.string().min(1) }).nullable(),
    variations: z.array(z.strictObject({ id: z.string().min(1), situation: z.string().min(1) })),
});
export const PracticeViewSchema = z.strictObject({
    exerciseId: z.string().min(1),
    brief: PracticeBriefViewSchema,
    state: PracticeStateSchema,
    /** The coach's authored feedback on the latest read attempt; empty before one. */
    reflection: z.array(z.string()),
    /** Whether the hint and the example may be shown now, and whether a worked revision or a variation is available. */
    can: z.strictObject({ attempt: z.boolean(), hint: z.boolean(), example: z.boolean(), revision: z.boolean(), variation: z.boolean(), pause: z.boolean(), resume: z.boolean(), conclude: z.boolean(), decline: z.boolean(), accept: z.boolean() }),
});
export const PracticeCurrentResponseSchema = z.strictObject({
    /** The exercise in progress (accepted, attempted or paused), or null. */
    current: PracticeViewSchema.nullable(),
    /** Exercises offered and not yet answered, newest first: a learner may pull up a few before choosing one (2026-09-23). */
    offers: z.array(PracticeViewSchema),
    /** Briefs the learner may ask for, by title and skill, so a choice is theirs. */
    available: z.array(z.strictObject({ briefId: z.string().min(1), title: z.string().min(1), dimension: RubricDimensionSchema, targetBehaviour: z.string().min(1) })),
});
export const PracticeOfferRequestSchema = z.strictObject({
    /** A brief chosen by the learner, or absent to let code pick one for the dimension or the plan. */
    briefId: z.string().min(1).optional(),
    dimension: RubricDimensionSchema.optional(),
});
export const PracticeAttemptRequestSchema = z.strictObject({
    text: z.string().min(1).max(4000),
    /** Client-generated; a retry with the same key returns the attempt already recorded. */
    idempotencyKey: z.string().min(1).max(120),
    /** The attempt this one revises, or absent. */
    revises: z.number().int().min(1).optional(),
});
/**
 * The coach's reply, and — when it chose to — one card it pushed onto the
 * front door (pedagogy review, decision 8). The coach may CHOOSE a case from
 * the real library for something the learner asked to work on; it can never
 * author one. The server validates the choice against the published shelf
 * and keeps it, so the card is there on the next visit too.
 */
export const CoachMessageResponseSchema = z.strictObject({
    reply: z.string(),
    card: CoachCardSchema.nullable(),
    /** The moments the coach pointed at in this reply, built by code from what it cited (C05). Absent on older replies. */
    references: EvidenceReferencesSchema.optional(),
    /** An exercise code offered because the learner asked to practise (C09); absent otherwise. Code, not the coach, offered it. */
    practice: PracticeViewSchema.nullable().optional(),
    /** The focus, when this message chose one or added a self-report to it (C08); absent otherwise. */
    focus: FocusViewSchema.nullable().optional(),
});
/**
 * GET /coach/messages — the learner's conversation with the coach, oldest
 * first, as it was kept. Kept for the learner to re-read, not for the coach:
 * every coach reply is still written over the record alone.
 */
export const CoachThreadMessageSchema = z.strictObject({
    id: z.string().min(1),
    from: z.enum(["you", "coach"]),
    text: z.string().min(1),
    at: z.iso.datetime(),
    /** The moments a coach line pointed at, kept with it so the control is there on a later visit. Absent on older lines. */
    references: EvidenceReferencesSchema.optional(),
});
export const CoachThreadSchema = z.strictObject({ messages: z.array(CoachThreadMessageSchema) });
export const HealthResponseSchema = z.strictObject({ status: z.literal("ok") });
//# sourceMappingURL=http.js.map